import styles from './ScenarioOne.module.scss';
import {
    selectClientSelected,
    selectDataIndex,
    selectFiltersAllValues,
    selectMarketValue,
    selectRmValue,
    selectScenarioOneOutliers,
    selectScenarioOnePortfolios,
    selectScenarioOneTrends,
    selectSeriesIndex,
    selectTeamValue,
    selectTimeslicerEndMonth,
    selectTimeslicerStartMonth,
    selectZoneValue
} from 'selectors/selectors';
import { areAllFilterValuesNumberValues } from 'utils/filters';
import { generateRequestParamsForScenarioApi } from 'utils/api';
import { ChartType, Scenarios, ScenarioType } from 'types/types';
import ContentContainer from 'components/content-container/ContentContainer';
import ChartWrapper from 'components/chart-wrapper/ChartWrapper';
import InfiniteTable from 'components/infinite-table/InfiniteTable';
import { detailedViewTable } from 'constants/scenarioOne';
import { useDispatch, useSelector } from 'react-redux';
import {
    forwardRef,
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState
} from 'react';
import ReactECharts from 'echarts-for-react';
import {
    optionsWhenClientIsNotSelected,
    optionsWhenClientIsSelected,
    scenarioOneNoResultTrendAnalysisChartOptions,
    scenarioOneRmLevelScatterChartOptions,
    scenarioOneTrendAnalysisChartOptions
} from 'utils/charts';
import {
    fetchOutliersData,
    fetchPortfolioData,
    fetchScenarioOneStats,
    fetchTrendData,
    resetScenarioOneCharts,
    updateClientSelected
} from 'slices/ScenarioOneSlice';
import MinimizeMaximizeArrows from 'components/minimize-maximize-arrows/MinimizeMaximizeArrows';
import { isNumber } from 'utils/common';

const ScenarioOne = forwardRef((props, ref) => {
    const dispatch = useDispatch();

    const [rmChartWidth, setRmChartWidth] = useState(null);
    const [rmChartHeight, setRmChartHeight] = useState(null);
    const [clientChartWidth, setClientChartWidth] = useState(null);
    const [isRmLevelChartInFullView, setIsRmLevelChartInFullView] =
        useState(false);
    const [isRmLevelChartInManualZoom, setIsRmLevelChartInManualZoom] =
        useState(false);
    const [defaultViewOutliers, setDefaultViewOutliers] = useState({});
    const [fullViewOutliers, setFullViewOutliers] = useState({});

    const [
        isPortfolioLevelChartInFullView,
        setIsPortfolioLevelChartInFullView
    ] = useState(false);
    const [
        isPortfolioLevelChartInManualZoom,
        setIsPortfolioLevelChartInManualZoom
    ] = useState(false);
    const [defaultViewPortfolios, setDefaultViewPortfolios] = useState({});
    const [fullViewPortfolios, setFullViewPortfolios] = useState({});

    // Selectors
    const tsStart = useSelector(selectTimeslicerStartMonth) || {};
    const tsEnd = useSelector(selectTimeslicerEndMonth) || {};
    const allValues = useSelector(selectFiltersAllValues);

    const zoneValue = useSelector(selectZoneValue);
    const marketValue = useSelector(selectMarketValue);
    const teamValue = useSelector(selectTeamValue);
    const rmValue = useSelector(selectRmValue);

    const clientSelected = useSelector(selectClientSelected);
    const selectedDataIndex = useSelector(selectDataIndex);
    const selectedSeriesIndex = useSelector(selectSeriesIndex);

    const outliers = useSelector(selectScenarioOneOutliers);
    const trends = useSelector(selectScenarioOneTrends);
    const portfolios = useSelector(selectScenarioOnePortfolios);

    const outliersHasData =
        outliers && outliers.outliers && outliers.outliers.length > 0;

    useEffect(() => {
        dispatch(resetScenarioOneCharts());
    }, []);

    useEffect(() => {
        if (tsStart && tsEnd) {
            if (
                areAllFilterValuesNumberValues({
                    startMonth: tsStart.epoch,
                    endMonth: tsEnd.epoch,
                    zone: zoneValue,
                    market: marketValue,
                    team: teamValue,
                    rm: rmValue
                })
            ) {
                dispatch(
                    fetchScenarioOneStats(
                        generateRequestParamsForScenarioApi(
                            ScenarioType.Stats,
                            Scenarios.One,
                            marketValue,
                            teamValue,
                            rmValue,
                            tsStart.epoch,
                            tsEnd.epoch
                        )
                    )
                );

                dispatch(
                    fetchOutliersData(
                        generateRequestParamsForScenarioApi(
                            ChartType.Outliers,
                            Scenarios.One,
                            marketValue,
                            teamValue,
                            rmValue,
                            tsStart.epoch,
                            tsEnd.epoch,
                            allValues,
                            zoneValue
                        )
                    )
                );

                dispatch(
                    fetchTrendData(
                        generateRequestParamsForScenarioApi(
                            ChartType.Trend,
                            Scenarios.One,
                            marketValue,
                            teamValue,
                            rmValue,
                            tsStart.epoch,
                            tsEnd.epoch
                        )
                    )
                );
            }
        }
        setIsRmLevelChartInFullView(false);
        setIsRmLevelChartInManualZoom(false);
    }, [marketValue, teamValue, rmValue, tsStart, tsEnd, zoneValue]);

    useEffect(() => {
        if (outliersHasData) {
            const rmOutliers = outliers.outliers;
            const outliersOrderAscByNetDelta =
                rmOutliers && rmOutliers.length > 0
                    ? [...rmOutliers].sort((a, b) => a.netDelta - b.netDelta)
                    : [];
            if (outliersOrderAscByNetDelta.length > 0) {
                const bottomPercentile = 10;
                const topPercentile = 90;
                const bottomIndex = Math.ceil(
                    (bottomPercentile / 100) * outliersOrderAscByNetDelta.length
                );
                const topIndex = Math.floor(
                    (topPercentile / 100) * outliersOrderAscByNetDelta.length
                );
                const defaultViewOutliers = outliersOrderAscByNetDelta.slice(
                    bottomIndex,
                    topIndex
                );
                if (isRmLevelChartInFullView) {
                    setDefaultViewOutliers({
                        outliers:
                            outliersOrderAscByNetDelta.length > 1
                                ? defaultViewOutliers
                                : outliersOrderAscByNetDelta
                    });
                    setFullViewOutliers(outliers);
                } else {
                    setFullViewOutliers(outliers);
                    setDefaultViewOutliers({
                        outliers:
                            outliersOrderAscByNetDelta.length > 1
                                ? defaultViewOutliers
                                : outliersOrderAscByNetDelta
                    });
                }
            }
        } else {
            setFullViewOutliers({});
            setDefaultViewOutliers({});
        }
    }, [outliers, isRmLevelChartInFullView]);

    useEffect(() => {
        if (portfolios && portfolios.length > 0) {
            const portfoliosOrderAscByNetDelta =
                portfolios && portfolios.length > 0
                    ? [...portfolios].sort((a, b) => a.netDelta - b.netDelta)
                    : [];
            if (portfoliosOrderAscByNetDelta.length > 0) {
                const bottomPercentile = 10;
                const topPercentile = 90;
                const bottomIndex = Math.ceil(
                    (bottomPercentile / 100) *
                        portfoliosOrderAscByNetDelta.length
                );
                const topIndex = Math.floor(
                    (topPercentile / 100) * portfoliosOrderAscByNetDelta.length
                );
                const defaultViewPortfolios =
                    portfoliosOrderAscByNetDelta.slice(bottomIndex, topIndex);
                if (isPortfolioLevelChartInFullView) {
                    setDefaultViewPortfolios(
                        portfoliosOrderAscByNetDelta.length > 1
                            ? defaultViewPortfolios
                            : portfoliosOrderAscByNetDelta
                    );
                    setFullViewPortfolios(portfolios);
                } else {
                    setFullViewPortfolios(outliers);
                    setDefaultViewPortfolios(
                        portfoliosOrderAscByNetDelta.length > 1
                            ? defaultViewPortfolios
                            : portfoliosOrderAscByNetDelta
                    );
                }
            }
        } else {
            setFullViewPortfolios({});
            setDefaultViewPortfolios({});
        }
    }, [portfolios, isPortfolioLevelChartInFullView]);

    const handleResize = () => {
        const getRmChartContainerElement = document.getElementById(
            's1RmLevelScatterChart'
        );
        const getClientChartContainerElement = document.getElementById(
            's1ClientLevelScatterChart'
        );
        setRmChartWidth(getRmChartContainerElement.offsetWidth);
        setRmChartHeight(getRmChartContainerElement.offsetHeight);
        setClientChartWidth(getClientChartContainerElement.offsetWidth);
    };

    useEffect(() => {
        window.addEventListener('resize', handleResize);
        handleResize();
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    useEffect(() => {
        if (clientSelected) {
            dispatch(
                fetchPortfolioData(
                    generateRequestParamsForScenarioApi(
                        ChartType.PortfolioBox,
                        Scenarios.One,
                        marketValue,
                        teamValue,
                        rmValue,
                        tsStart.epoch,
                        tsEnd.epoch,
                        null,
                        zoneValue,
                        clientSelected
                    )
                )
            );
        }
    }, [clientSelected]);

    const onClick = (params) => {
        const { name: ctoCode, seriesIndex, dataIndex } = params;
        const eChartsInstance = ref.current.getEchartsInstance();
        eChartsInstance.dispatchAction({
            type: 'select',
            seriesIndex: seriesIndex,
            dataIndex: dataIndex
        });
        dispatch(
            updateClientSelected({
                clientSelected: ctoCode,
                seriesIndex,
                dataIndex
            })
        );
        setIsPortfolioLevelChartInFullView(false);
        setIsPortfolioLevelChartInManualZoom(false);
    };

    const onSelectChanged = (params) => {
        const { fromAction, selected, fromActionPayload } = params;
        const { dataIndexInside, seriesIndex } = fromActionPayload;
        if (fromAction === 'select' && selected.length > 1) {
            if (ref.current) {
                const eChartsInstance = ref.current.getEchartsInstance();
                const previouslySelected = selected.filter(
                    (s) =>
                        s.dataIndex !== [dataIndexInside] &&
                        s.seriesIndex !== seriesIndex
                );
                eChartsInstance.dispatchAction({
                    type: 'unselect',
                    seriesIndex: previouslySelected[0].seriesIndex,
                    dataIndex: previouslySelected[0].dataIndex
                });
            }
        }
    };

    const onRmChartDataZoom = () => {
        reselectRmOnManualZoomOrChangeView();
        setIsRmLevelChartInManualZoom(true);
    };

    const onRmLevelScatterChartEvents = {
        click: onClick,
        selectChanged: onSelectChanged,
        datazoom: onRmChartDataZoom
    };

    const updateEventHandlers = useCallback(() => {
        if (ref.current) {
            const chartInstance = ref.current.getEchartsInstance();
            const eventHandlers = onRmLevelScatterChartEvents;

            for (const eventName in eventHandlers) {
                chartInstance.off(eventName);
                chartInstance.on(eventName, eventHandlers[eventName]);
            }
        }
    }, [ref, onRmLevelScatterChartEvents, clientSelected]);

    useEffect(() => {
        updateEventHandlers();
    }, [clientSelected, updateEventHandlers]);

    const memoizedRmLevelScatterChart = useMemo(() => {
        return (
            <ReactECharts
                option={scenarioOneRmLevelScatterChartOptions(
                    rmChartWidth,
                    rmChartHeight,
                    isRmLevelChartInFullView
                        ? fullViewOutliers
                        : defaultViewOutliers,
                    isRmLevelChartInManualZoom,
                    isRmLevelChartInFullView
                )}
                style={{ width: '100%', height: '100%' }}
                onEvents={onRmLevelScatterChartEvents}
                notMerge={true}
                ref={ref}
            ></ReactECharts>
        );
    }, [
        rmChartWidth,
        rmChartHeight,
        ref,
        fullViewOutliers,
        defaultViewOutliers,
        isRmLevelChartInFullView,
        isRmLevelChartInManualZoom
    ]);

    const findSeriesIndexOfRmAndReturnFilteredRms = (outliers) => {
        const greenRms =
            outliers &&
            outliers.filter((o) => {
                return o.outlier === 0 && o.emergingOutlier === 0;
            });
        const yellowRms =
            outliers &&
            outliers.filter((o) => {
                return o.outlier === 0 && o.emergingOutlier === 1;
            });
        const redRms =
            outliers &&
            outliers.filter((o) => {
                return o.outlier === 1 && o.emergingOutlier === 1;
            });
        if (greenRms.find((grm) => grm.rmEmployeeId === clientSelected)) {
            return {
                outliers: greenRms,
                seriesIndex: 0
            };
        }
        if (yellowRms.find((yrm) => yrm.rmEmployeeId === clientSelected)) {
            return {
                outliers: yellowRms,
                seriesIndex: 1
            };
        }
        if (redRms.find((rrm) => rrm.rmEmployeeId === clientSelected)) {
            return {
                outliers: redRms,
                seriesIndex: 2
            };
        }
        return null;
    };

    const reselectRmOnManualZoomOrChangeView = () => {
        const { outliers: defaultOutliers } = { ...defaultViewOutliers };
        const { outliers: fullOutliers } = { ...fullViewOutliers };
        if (
            clientSelected &&
            ref.current &&
            defaultOutliers &&
            defaultOutliers.length > 0 &&
            fullOutliers &&
            fullOutliers.length > 0
        ) {
            const eChartsInstance = ref.current.getEchartsInstance();
            const isClientSelectedFoundInDefaultView = defaultOutliers.find(
                (o) => o.rmEmployeeId === clientSelected
            );
            if (
                (!isRmLevelChartInFullView &&
                    isClientSelectedFoundInDefaultView) ||
                isRmLevelChartInFullView ||
                isPortfolioLevelChartInManualZoom
            ) {
                const outliersSetToUse = isRmLevelChartInFullView
                    ? fullOutliers
                    : defaultOutliers;
                if (findSeriesIndexOfRmAndReturnFilteredRms(outliersSetToUse)) {
                    const {
                        seriesIndex: calculatedSeriesIndex,
                        outliers: calculatedOutliers
                    } =
                        findSeriesIndexOfRmAndReturnFilteredRms(
                            outliersSetToUse
                        );
                    const calculatedDataIndex = calculatedOutliers.findIndex(
                        (o) => o.rmEmployeeId === clientSelected
                    );
                    if (
                        isNumber(calculatedSeriesIndex) &&
                        isNumber(calculatedDataIndex)
                    ) {
                        eChartsInstance.dispatchAction({
                            type: 'select',
                            seriesIndex: calculatedSeriesIndex,
                            dataIndex: calculatedDataIndex
                        });
                    }
                }
            }
        }
    };

    useEffect(() => {
        reselectRmOnManualZoomOrChangeView();
    }, [
        selectedDataIndex,
        selectedSeriesIndex,
        clientSelected,
        isRmLevelChartInManualZoom,
        isRmLevelChartInFullView,
        outliers,
        ref,
        fullViewOutliers,
        defaultViewOutliers
    ]);

    const onPortfolioChartDataZoom = () => {
        setIsPortfolioLevelChartInManualZoom(true);
    };

    const onPortfolioLevelScatterChartEvents = {
        datazoom: onPortfolioChartDataZoom
    };
    const memoizedClientLevelScatterChart = useMemo(() => {
        return (
            <ReactECharts
                option={
                    clientSelected
                        ? optionsWhenClientIsSelected(
                              clientChartWidth,
                              rmChartHeight,
                              clientSelected,
                              isPortfolioLevelChartInFullView
                                  ? fullViewPortfolios
                                  : defaultViewPortfolios,
                              outliers,
                              isPortfolioLevelChartInManualZoom,
                              isPortfolioLevelChartInFullView
                          )
                        : optionsWhenClientIsNotSelected()
                }
                style={{ width: '100%', height: '100%' }}
                onEvents={onPortfolioLevelScatterChartEvents}
                notMerge={true}
            ></ReactECharts>
        );
    }, [
        clientSelected,
        rmChartWidth,
        rmChartHeight,
        fullViewPortfolios,
        defaultViewPortfolios,
        isPortfolioLevelChartInFullView,
        isPortfolioLevelChartInManualZoom
    ]);

    const chartRef = useRef(null);
    useEffect(() => {
        chartRef.current.getEchartsInstance().dispose();
        const validData = trends
            ? trends.filter(
                  (d) =>
                      d.month > 0 &&
                      d.year > 0 &&
                      isNumber(d.negativeDelta) &&
                      isNumber(d.totalRevenue)
              )
            : [];

        if (validData.length > 0) {
            chartRef.current
                .getEchartsInstance()
                .setOption(scenarioOneTrendAnalysisChartOptions(trends));
        } else {
            chartRef.current
                .getEchartsInstance()
                .setOption(scenarioOneNoResultTrendAnalysisChartOptions());
        }
    }, [trends]);
    return (
        <ContentContainer>
            <div className={styles.chartsContainer}>
                <ChartWrapper
                    id={'s1RmLevelScatterChart'}
                    comboClass={styles.rightMargin}
                    style={{ position: 'relative' }}
                >
                    {memoizedRmLevelScatterChart}
                    {outliersHasData && (
                        <div className={styles.arrowsContainer}>
                            <MinimizeMaximizeArrows
                                isManualZoom={isRmLevelChartInManualZoom}
                                isFullView={isRmLevelChartInFullView}
                                toggleDefaultView={() =>
                                    setIsRmLevelChartInFullView(false)
                                }
                                toggleFullView={() =>
                                    setIsRmLevelChartInFullView(true)
                                }
                                toggleManualZoom={setIsRmLevelChartInManualZoom}
                            />
                        </div>
                    )}
                    {outliers &&
                        outliers.outliers &&
                        outliers.outliers.length === 0 && (
                            <div className={styles.noResultsContainer}>
                                No Results
                            </div>
                        )}
                </ChartWrapper>

                <ChartWrapper
                    id={'s1ClientLevelScatterChart'}
                    comboClass={styles.rightMargin}
                    style={{ position: 'relative' }}
                >
                    {memoizedClientLevelScatterChart}
                    {clientSelected && (
                        <div className={styles.arrowsContainer}>
                            <MinimizeMaximizeArrows
                                isManualZoom={isPortfolioLevelChartInManualZoom}
                                isFullView={isPortfolioLevelChartInFullView}
                                toggleDefaultView={() =>
                                    setIsPortfolioLevelChartInFullView(false)
                                }
                                toggleFullView={() =>
                                    setIsPortfolioLevelChartInFullView(true)
                                }
                                toggleManualZoom={
                                    setIsPortfolioLevelChartInManualZoom
                                }
                            />
                        </div>
                    )}
                </ChartWrapper>

                <ChartWrapper>
                    <ReactECharts
                        option={
                            trends &&
                            trends.filter(
                                (d) =>
                                    d.month > 0 &&
                                    d.year > 0 &&
                                    isNumber(d.negativeDelta) &&
                                    isNumber(d.totalRevenue)
                            ).length > 0
                                ? scenarioOneTrendAnalysisChartOptions(trends)
                                : scenarioOneNoResultTrendAnalysisChartOptions()
                        }
                        style={{ width: '100%', height: '100%' }}
                        ref={chartRef}
                    ></ReactECharts>
                </ChartWrapper>
            </div>
            <div className={styles.tableContainer}>
                <div className={styles.tableTitle}>Detailed View</div>
                <InfiniteTable
                    zone={zoneValue}
                    market={marketValue}
                    team={teamValue}
                    rm={rmValue}
                    start={tsStart}
                    end={tsEnd}
                    allValues={allValues}
                    columns={detailedViewTable}
                    scenario={Scenarios.One}
                />
            </div>
        </ContentContainer>
    );
});

ScenarioOne.displayName = 'ScenarioOne';
export default ScenarioOne;
